import { useEffect, useState } from "react";

/**
 * Shared install hook. Captures the `beforeinstallprompt` event globally in
 * /app/frontend/src/index.js (stored on window.__hnDeferredPrompt) and exposes
 * install state + trigger to any component.
 */
export default function useInstallPrompt() {
  const [canInstall, setCanInstall] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isIOSNonSafari, setIsIOSNonSafari] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const ua = window.navigator.userAgent || "";
    // Detect any iOS device — iPhone, iPad, or iPod. iPadOS 13+ Safari may
    // send a desktop UA, so we also fall back to a Mac + touch-support check.
    const isIOSDevice =
      /iPhone|iPad|iPod/i.test(ua) ||
      (/Mac/i.test(ua) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1);
    // Detect if the current browser is Safari (only Safari supports "Add to
    // Home Screen" on iOS — Chrome/Firefox on iOS use the WebKit shell but the
    // install option isn't exposed to them).
    const isInAppOrOtherIOSBrowser = /CriOS|FxiOS|EdgiOS|Instagram|FBAN|FBAV|Line|Twitter|WhatsApp/i.test(ua);
    // We show iOS instructions whenever it's an iOS device — even in Chrome-iOS
    // / in-app browsers — because the user's only path is to reopen in Safari.
    setIsIOS(isIOSDevice);
    setIsIOSNonSafari(isIOSDevice && isInAppOrOtherIOSBrowser);

    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Prompt might already have been captured before this component mounted.
    if (window.__hnDeferredPrompt) setCanInstall(true);

    const onAvailable = () => setCanInstall(true);
    const onDone = () => setCanInstall(false);
    window.addEventListener("hn-install-available", onAvailable);
    window.addEventListener("hn-install-done", onDone);
    return () => {
      window.removeEventListener("hn-install-available", onAvailable);
      window.removeEventListener("hn-install-done", onDone);
    };
  }, []);

  const promptInstall = async () => {
    const dp = window.__hnDeferredPrompt;
    if (!dp) return "unavailable";
    try {
      dp.prompt();
      const { outcome } = await dp.userChoice;
      window.__hnDeferredPrompt = null;
      setCanInstall(false);
      return outcome; // "accepted" | "dismissed"
    } catch {
      return "error";
    }
  };

  return { canInstall, isIOS, isIOSNonSafari, isStandalone, promptInstall };
}
